import { useState, useRef, useEffect, useMemo } from "react";
import type { FocusEvent } from "react";
import PiChevronDownStroke from "@markeroo/icons/src/stroke/arrows-&-chevrons/PiChevronDownStroke";
import { inputClass, zIndex } from "@/styles/variants";
import { ui } from "@/strings";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  emptyText?: string;
  createNewOption?: {
    label: string;
    value: string;
    onSelect: () => void;
  };
  onCreateWithName?: (searchText: string) => void;
  disabled?: boolean;
  required?: boolean;
  "aria-label"?: string;
  "data-tutorial-target"?: string;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "",
  emptyText = ui.empty.noOptionsFound,
  createNewOption,
  onCreateWithName,
  disabled = false,
  required = false,
  "aria-label": ariaLabel,
  "data-tutorial-target": dataTutorialTarget,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const ignoreNextFocusRef = useRef(false);


  // Get selected option
  const selectedOption = options.find((opt) => opt.value === value);

  // Sort options alphabetically and filter by search text
  const filteredOptions = useMemo(() => {
    const sorted = [...options].sort((a, b) => a.label.localeCompare(b.label));
    if (!searchText.trim()) return sorted;

    // If search text matches the selected option exactly, show all (user focused input)
    if (selectedOption && searchText === selectedOption.label) {
      return sorted;
    }

    return sorted.filter((opt) =>
      opt.label.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [options, searchText, selectedOption]);

  const hasMatches = filteredOptions.length > 0;
  const shouldAutoSelectCreate = !hasMatches && createNewOption;

  // Get display value for the input
  const displayValue = isOpen ? searchText : selectedOption?.label ?? "";

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions.length, shouldAutoSelectCreate]);

  // Blur input when value changes externally (not through handleSelectOption)
  // This handles cases like auto-selecting a newly created student
  useEffect(() => {
    if (!isOpen && value && inputRef.current && inputRef.current === document.activeElement) {
      // Use setTimeout to blur after React's render cycle completes
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }, 0);
    }
  }, [value, isOpen]);

  // Click outside handler
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchText("");
      }
    };

    // Small delay to let the dropdown render before attaching listener
    const timer = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isOpen]);

  const handleInputFocus = () => {
    if (ignoreNextFocusRef.current) {
      ignoreNextFocusRef.current = false;
      return;
    }

    // Pre-fill search text with current value so it doesn't appear empty
    if (value) {
      const option = options.find((opt) => opt.value === value);
      if (option) {
        setSearchText(option.label);
      }
    }

    setIsOpen(true);
  };

  const handleInputBlur = (e: FocusEvent) => {
    // Don't close if focus moves inside the component
    if (dropdownRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }
    // Don't close if relatedTarget is null - this happens when clicking dropdown
    // buttons with preventDefault on mousedown. The click handler will manage
    // closing the dropdown appropriately via handleSelectOption.
    if (e.relatedTarget === null) {
      return;
    }
    setIsOpen(false);
    setSearchText("");
  };

  const handleInputClick = () => {
    // Select all text when clicking the input
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setIsOpen(true);
  };

  const handleSelectOption = (optionValue: string) => {
    // Prevent the next focus event (triggered by click) from reopening the dropdown
    ignoreNextFocusRef.current = true;
    if (optionValue === createNewOption?.value) {
      // Handle create new option
      if (onCreateWithName && searchText.trim()) {
        onCreateWithName(searchText.trim());
      }
      createNewOption.onSelect();
    } else {
      onChange(optionValue);
    }
    setIsOpen(false);
    setSearchText("");
    // Blur after state updates to ensure it happens after React re-renders
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
      // Reset the ignore flag so future focuses work (e.g. user clicking back into the input)
      ignoreNextFocusRef.current = false;
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    const allOptions = hasMatches ? filteredOptions : [];
    const totalOptions = allOptions.length + (createNewOption ? 1 : 0);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (totalOptions > 0) {
          setHighlightedIndex((prev) => (prev + 1) % totalOptions);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (totalOptions > 0) {
          setHighlightedIndex((prev) => (prev - 1 + totalOptions) % totalOptions);
        }
        break;
      case "Enter":
        e.preventDefault();
        if (shouldAutoSelectCreate) {
          // Auto-selected create option
          handleSelectOption(createNewOption.value);
        } else if (highlightedIndex < allOptions.length) {
          // Regular option
          handleSelectOption(allOptions[highlightedIndex].value);
        } else if (createNewOption && highlightedIndex === allOptions.length) {
          // Create option at the end
          handleSelectOption(createNewOption.value);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSearchText("");
        inputRef.current?.blur();
        break;
    }
  };

  // Generate ID for the currently highlighted option
  const getOptionId = (index: number) => `searchable-select-option-${index}`;
  const totalOptions = (hasMatches ? filteredOptions.length : 0) + (createNewOption ? 1 : 0);
  const highlightedOptionId = isOpen && totalOptions > 0 ? getOptionId(highlightedIndex) : undefined;

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-label={ariaLabel}
          data-tutorial-target={dataTutorialTarget}
          className={`${inputClass} pr-10`}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls="searchable-select-dropdown"
          aria-autocomplete="list"
          aria-activedescendant={highlightedOptionId}
        />
        <PiChevronDownStroke
          size={20}
          className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-text-theme-muted transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {isOpen && (
        <div
          id="searchable-select-dropdown"
          role="listbox"
          className="absolute mt-1 max-h-60 w-full overflow-auto rounded-lg border border-border bg-white shadow-lg"
          style={{ zIndex: zIndex.dropdown }}
        >
          {filteredOptions.map((option, index) => (
            <button
              key={option.value}
              id={getOptionId(index)}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelectOption(option.value)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`w-full px-4 py-2 text-left transition ${
                highlightedIndex === index
                  ? "bg-brand-50 text-brand-900"
                  : "text-text-theme-primary hover:bg-surface-secondary"
              }`}
              role="option"
              aria-selected={value === option.value}
            >
              {option.label}
            </button>
          ))}

          {createNewOption && (
            <button
              key={createNewOption.value}
              id={getOptionId(filteredOptions.length)}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelectOption(createNewOption.value)}
              onMouseEnter={() => setHighlightedIndex(filteredOptions.length)}
              className={`w-full px-4 py-2 text-left font-medium transition ${
                hasMatches ? "border-t border-border" : ""
              } ${
                highlightedIndex === filteredOptions.length || shouldAutoSelectCreate
                  ? "bg-brand-50 text-brand-900"
                  : "text-primary hover:bg-surface-secondary"
              }`}
              role="option"
              aria-selected={false}
            >
              {createNewOption.label}
            </button>
          )}

          {!hasMatches && !createNewOption && (
            <div className="px-4 py-2 text-sm text-text-theme-muted">{emptyText}</div>
          )}
        </div>
      )}
    </div>
  );
}
